import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CONFIRMATION_MODE, WIZ_PHYSIC_POSITION, CONFIRMATION_RESULT  } from '@wizeoo/akm-core-lib';
import { PopUpManager, WizPopUp, WizIcon, WizRoot, ConfirmationResultObj, WizEventManager, WizEvent, WizVgroup} from '@wizeoo/akm-component-lib';
import { BaselineEngine, ComputeResult, DataLoadObj, EM_ACTION, FeiKeyDef, FormulaCoreEngine, IBaselineValue, LoopResult, MatrixCallParam, MeasMode, MeasureCoreEngine, Triggers, WidgetCell, WizConstants } from '@wizeoo/akm-compute-lib';
import { Baseline, BaselineValue, Measure, VisionKey, VisionStep, WidgetSelect } from '@wizeoo/akm-message-lib';
import { WidgetConfigurationObj, WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { WidgetLine } from '@wizeoo/akm-compute-lib';
import { Column } from 'ag-grid-community';
import { clone, cloneDeep } from 'lodash-es';

import { IForm } from '../../../manager/widgets/i-form';
import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { ViewData } from '../../../model/view.data';
import { ChronocodeVO } from '../../../vo/core/chronocode.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { PopUtils } from '../../../framework/components/pop/pop-utils';
import { FormExtendedPop } from './form-extended.pop';
import { BaselineFormChoosePop, BaselineFormChooserParams } from './pops/baseline-form-choose.pop';
import { IWidget } from '../../../manager/widgets/i-widget';
import { UpdateEntry, CompositeSourcePack } from '../../../command/core/sync/sync.service';
import { WidgetBasic } from '../base/widget-basic';
import { NextStepPivot } from '../../tools/next-step-pivot.component';
import { FastSelectData } from './../../../manager/widgets/widget.engine';
import { AlkEvent } from '../../../framework/events/alk.event';
import { FeiVO } from '../../../vo/workingdata/fei.vo';
import { FormFieldObj } from '../../../manager/baselines/form-field.obj';
import { FormItemObj } from '../../../manager/baselines/form-item.obj';
import { WizColDef } from '../../../manager/workingdata/fei.engine';
import { GridsterWidgetItem } from '../../../vo/workingzone/gridster-widget-item';
import { BaselineGridsterComponent } from './baseline-gridster.component';


/**
 * objet de communication entre le formulaire et ses champs
 */
export interface FormUpdate {
  field: FormFieldObj; // ffo cible, le champ
  item: FormItemObj; // fio, valeur sélectionnée
}

/**
 * suivi des modifications de clé pour transmission entre fonctions
 */
export interface FormKeyUpdate {
  // badKey: boolean; // la clé est incomplète (creation uniquement)
  keyUpdates: Map<string, string>; // éléments de clés modifiés (clé = valeur)
  filterKeyUpdates: Map<string, string>; // map des filterKeys modifiées
  measureUpdates: Map<string, string>;// map de modifs de mesures
  initMode: boolean; // mode init, je modifie des éléments de la clé (pour jouer les valeurs par défaut)
}


@Component({
  selector: 'form-widget',
  templateUrl: './form.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** formulaire de saisie d'une nouvelle baseline */
export class FormWidget extends WidgetBasic implements AfterViewInit, OnDestroy, IForm {

  /** constructeur, rien de spécial */
  constructor(elementRef: ElementRef, public cdRef: ChangeDetectorRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.FORM;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = FormExtendedPop;
  }

  public currentWidgetLine: WidgetLine = null; // la ligne actuellement sélectionnée
  public clone: WidgetLine = null; // clone de travail

  public get cloneExist():boolean {
    return !!this.currentWidgetLine?.bl?.id;
  }

  public fakeFei: FeiVO = null; // fei Fake pour gestion des contrôles de cohérence

  /** valeurs par défaut et triggers pour chaque champ, par clé de champ#level */
  public defaultMap: Map<string, Triggers> = new Map();
  public triggersMap: Map<string, Triggers> = new Map();
  /** trigger inversé : les mesures sources avec les mesures à déclencher */
  public reverseTriggersMap: Map<string, Set<Triggers>> = null;


  /** map des champs alimentés et leur valeur. Par exemple CHRONO#3 = VAL */
  public keyPartMap: Map<string, string> = null;
  /** map des champs alimentés et leur valeur. sur INIT de la WidgetLine (sur le doSelect) */
  public initKeyPartMap: Map<string, string> =null;

  /** traçabilité des messages modifiés */
  public updatedMessage: Map<string, string> =null;

  /* ##############################################################################
                  composants spécifiques et marqueurs d'état IHM
  ############################################################################## */

  // zone d'ajout des champs de saisie
  @ViewChild('icoSelect', { read: WizIcon }) public icoSelect: WizIcon;
  @ViewChild('icoDelete', { read: WizIcon }) public icoDelete: WizIcon;
  @ViewChild('argsZone', { read: WizVgroup }) argsZone: WizVgroup; // zone d'édition des args
  @ViewChild('baselineGridster') baselineGridster: BaselineGridsterComponent; // le gridster
  @ViewChild('agNextStep') agNextStep: NextStepPivot; // pivot de simulation

  public get specifMode(): boolean {
    // let realWidth: number = 
    // console.log(this.elementRef?.nativeElement?.offsetWidth);
    return WizRoot.specifMode || this.elementRef?.nativeElement?.offsetWidth < 300;
  }

  public get widget(): IWidget {
    return this;
  }

  /** les éléments physiques des champs, pour alimentation et positionnement dans le gridster */
  public widgetItems: GridsterWidgetItem[];

  private _showSelect: boolean = true;
  // autoriser la zone de sélection
  @Input() public set showSelect(value: boolean) {
    this._showSelect = value;
    this.cdRef.markForCheck();
  }
  public get showSelect(): boolean {
    return this.settings.showR && this._showSelect;
  }

  private _showAdd: boolean = true;
  // autoriser la zone d'ajout
  @Input() public set showAdd(value: boolean) {
    this._showAdd = value;
    this.cdRef.markForCheck();
  }
  public get showAdd(): boolean {
    // prise en compte d'un filtre global obligatoire manquant
    let emptyMand: boolean = this.mandatoryGlobalEmpty(); 
    // console.log('showAdd', emptyMand);
    return this.settings.showC && this._showAdd && !emptyMand;
    /// return this._showAdd;
  }

  private _showDelete: boolean = true;
  // autoriser la zone de suppression
  @Input() public set showDelete(value: boolean) {
    this._showDelete = value;
    this.cdRef.markForCheck();
  }
  public get showDelete(): boolean {
    return this.settings.showD && this._showDelete && this.cloneExist;
  }

  /** annule la sélection sur un formulaire */
  @Input() public showRevert: boolean = true;

  /** par défaut, on sauvegarde pas. Tous les champs doivent être OK */
  public saveDisabled: boolean = false;

  /** à minima une modif */
  private _hasUpdate: boolean = false;
  public get hasUpdate(): boolean {
    return this._hasUpdate;
  }
  public set hasUpdate(value: boolean) {  
    this._hasUpdate = value;
  }



  /** mode création, la BL n'a pas d'ID */
  public get creationMode(): boolean {
    return !!this.currentWidgetLine?.bl && !this.currentWidgetLine.bl.id;
  }

  /** mode modification */
  public get currentWidgetLineExist(): boolean {
    return !!this.currentWidgetLine?.bl?.id;
  }


  /* ##############################################################################
        sélection d'un élément existant
  ############################################################################## */

  /** popUp de sélection d'un élément */
  private choosePop: WizPopUp;

  /** sélection depuis la dropDown */
  public askForSelect(e: Event): void {
    if (PopUpManager.exist(this.choosePop)) {
      this.choosePop.hide(); // suppression de l'instance existante
    }

    // construction des données pour la popUp de sélection d'élément
    const datas: BaselineFormChooserParams = {
      updateCallback: this.backFromSelectionPop.bind(this), // la callback pour retour
      widget: this, // le pivot source pour prise en compte du mode "conf"
      sourceDS: this.mainDS // la liste source
    }

    // ouverture de la pop de sélection de WidgetLine
    this.choosePop = new WizPopUp(
      BaselineFormChoosePop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      BaselineFormChoosePop.baseWidth,
      BaselineFormChoosePop.baseHeight,
      datas
    );

    const pos: DOMRect = this.icoSelect.elementRef.nativeElement.getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, this.choosePop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    this.choosePop.customPosition = targetPosition;
    this.choosePop.show();
  }

  /**
   * retour de popUp de sélection du formulaire
   * @param widgetLine 
   */
  private backFromSelectionPop(widgetLine: WidgetLine): void {
    if(!!widgetLine) {
      this.doSelect(widgetLine); // création du clone de travail
    }
  }

  /** ajout autorisée */
  public mayAdd(): boolean {
    return this.fei?.canCreateItem([], true);
  }

  /** modif autorisée */
  public mayUpdate(): boolean {
    return true;
  }


  /** demande d'ajout d'un nouvel élément */
  public askForAdd(): void {
    this.model.widgetEngine.buildFakeWidgetLine(this.fei, this.doSelect.bind(this));
    // this.doSelect(proto); // on crée un fake 
  }

  /** demande de clonage */
  public askForClone(): void {
    
    // prise des champs exclus du clonage
    let excludeSet: Set<string> = new Set();
    this.context.ff.forEach(wcio => {
      if(wcio.noc) {
        excludeSet.add(wcio.cod);
      }
    });

    // reprise des clés éligibles sur la keyPartMap
    let protoPart: Map<string, string> = new Map();
    [...this.keyPartMap.keys()].forEach(key => {
      if(!excludeSet.has(key)) {
        
        // contrôle des séquences sur le même level
        let fkd: FeiKeyDef = this.fei?.keyMap?.get(key);
        let avoidSequence: boolean = false;
        if(fkd?.isSequence) {
          let srcStepKeys: VisionKey[] = BaselineEngine.getStepKeys(this.fei.vision, this.fei.visionStep);
          srcStepKeys.forEach(vk => {
            if(vk.code === key && vk.level === this.fei.visionStepLevel) {
              // console.log('fkd', fkd, this.fei.visionKeys);
              avoidSequence = true;
            }
          });
        }
        
        if(!avoidSequence) {
          let val: any = this.keyPartMap.get(key);
          if(val?.length > 0) {
            protoPart.set(key, val);
          }
        }

      }

      // NE PAS reprendre les séquences de même niveau

    });
    

    let candidateBaseline: Baseline = this.model.widgetEngine.buildBaselineFromPrototype(this.fei, protoPart);
    let proto: WidgetLine = this.model.widgetEngine.buildWidgetLinePrototype(this.fei, candidateBaseline);
    proto['cloning'] = true;
    proto['cloneParts'] = protoPart; // on injecte les parts sur le prototype de wl


    this.doSelect(proto); // on crée un fake 

    setTimeout(() => {
      this.checkSave()
    }, 0);
    // console.log('clonage', proto);
  }

  /** 
   * selection par 2x clic sur un grid externe
   * @param e alkEvent.PIVOT_DOUBLE
   * data = [fei, widgetLine]
   */
  private fastSelect(e: AlkEvent): void {
    let data: FastSelectData = e.data;
    const sourceFei: FeiVO = data.fei;
    if(data.kind === WIDGET_GLOBAL_KIND.BASELINE) {
      const fromWidgetLine: WidgetLine = data.data as WidgetLine;
    if(!!sourceFei && !!fromWidgetLine && this.state >= WIDGET_STATE.READY) {
      if (sourceFei.visionStepLevel === this.fei?.visionStepLevel) { // même step
        const widgetLine: WidgetLine = this.mainDS.getItemByKey(fromWidgetLine.key);
        
        if (widgetLine) {
          this.doSelect(widgetLine);
        }
      }
    }
    }
  }

  /** 
   * injection depuis calendar.addPop
   * @param e alkEvent.PIVOT_DOUBLE
   * data = [fei, widgetLine]
   */
  public injectWidgetLine(e: AlkEvent): void {
    // TODO TODO injecter aussi la date de départ
    const sourceFei: FeiVO = e.data[0] as FeiVO;
    const fromWidgetLine: WidgetLine = e.data[1] as WidgetLine;
    if(!!sourceFei && this.state >= WIDGET_STATE.READY) {
      if (sourceFei.visionStepLevel === this.fei?.visionStepLevel) { // même step
        const widgetLine: WidgetLine = this.mainDS.getItemByKey(fromWidgetLine?.key);
        
        if (widgetLine) {
          this.doSelect(widgetLine);
        } else {
          this.askForAdd();
        }
      }
    }
  }

  /**
   * rinçage de la sélection, sur doSelect à vide ou Init
   */
  private reset(): void {
    this.currentWidgetLine = null;
    this.mainDS.clearSelection();
    this.resetClone();
    this.cdRef.markForCheck();
  }

  /** 
   * sélection réelle, possible passage à "pas de sélection"
   * ou possible WidgetLine "carcasse vide" en mode création
   * @param widgetLine sélectionnée
   * @param force pour forcer le refresh sur retour d'update
   */
  private doSelect(widgetLine: WidgetLine, force: boolean = false): void {   
    
    if(this.state < WIDGET_STATE.READY) {
      return; // pas prêt
    }
    
    if(!widgetLine) { // déselection, en fait
      this.reset();
      this.alterContent(WIDGET_ACTION_ORIGIN.SELECT); // pour refresh graphique
      return;
    }

    if(widgetLine === this.currentWidgetLine && !force) { // self
      return; // on fait rien
    }

    // création du FEI, c'est lui qui portera les calculs
    this.fakeFei = this.model.dataEngine.generateFakeFEI(this.fei) as FeiVO;  
 

    this.currentWidgetLine = widgetLine;
    this.mainDS.select([this.currentWidgetLine]); 
    this.feedClone(this.currentWidgetLine, this.fakeFei); // creation du clone de travail

    this.alterContent(WIDGET_ACTION_ORIGIN.SELECT);

    
    
    if(this.showNextStep) { // dimensionnement optimal pour le grid en réduisant la taille du gridster
      setTimeout(() => {
        // console.log('form draw', this.baselineGridster.realHeight);
        this.baselineGridster.el.nativeElement.style.height = this.baselineGridster.realHeight + 'px';
        this.baselineGridster.el.nativeElement.style.minHeight = this.baselineGridster.realHeight + 'px';
      this.cdRef.markForCheck();
      }, 0);
      
    }
  }

  /**
   * remise à 0 des valeurs de travail
   */
  private resetClone(): void {
    this.clone = null;
    this.initKeyPartMap = null;
    this.keyPartMap = null;
    this.updatedMessage = null;
  }

  /**
   * alimentation du clone de travail
   * @param widgetLine 
   * @param fei, le fake sur scenar @@FAKE
   */
  private feedClone(widgetLine: WidgetLine, fei: FeiVO): void { 
    
    // on clone la baseline d'origine
    let cloneBaseline: Baseline = cloneDeep(widgetLine.bl);
    
    // alimentation du scenario sur la BL et les BLS
    cloneBaseline.scen = WizConstants.SCEN_FAKE_KEY_CODE;
    if(!cloneBaseline.values) { // on s'assure d'avoir des values
      cloneBaseline.values = {};
    }
    Object.values(cloneBaseline.values).forEach((blv: BaselineValue) => { // scenar @@Fake sur les BLVs
      blv.scen = WizConstants.SCEN_FAKE_KEY_CODE;
    });

    // reset des maps de clés
    this.initKeyPartMap = new Map();
    this.updatedMessage = new Map();
    
    // alimentation des maps de valeurs du formulaire depuis la VRAI WidgetLine
    let rawVal: string = null; // la valeur pour la clé de FEI
    
    // alimentation des cellules sur la wl
    if(!widgetLine.cells) {
      widgetLine.cells = new Map();
    }
    
    // détection d'un clonage en cours
    let cloning: boolean = false;
    if(!!widgetLine['cloning']) {
      // console.log("yes, cloning...", widgetLine, this.clone);
      cloning = true;
    }


    this.fei.keys.forEach((fkd: FeiKeyDef) => {
      rawVal = WizConstants.wildcardDomain;
      if(fkd.isMeasure || fkd.isFilterKey) { // à null pour les facultatifs
        rawVal = null;
      }
      if(widgetLine.bl) {
        if(!widgetLine.cells.get(fkd.fullCode)) {
          widgetLine.cells.set(fkd.fullCode, { k: widgetLine.key, m: fkd.fullCode, l: widgetLine.level, sc: widgetLine.bl.scen });
        }

        rawVal = BaselineEngine.getRawKeyOrValue(fei, this.currentWidgetLine, fkd.fullCode); 
      }

      // c'est une valeur héritée d'un scenar précédent, je me l'ajoute
      if(rawVal && !cloneBaseline.values[fkd.fullCode]) { 
        BaselineEngine.baselineSetValue(cloneBaseline, fkd.fullCode, rawVal);
      }

      // filtre user sur l'élément de clé : injection de la valeur
      if(!!this.model.widgetEngine.userGlobalFilter.has(fkd.fullCode)) { // il existe une restriction sur cette clé
        let vals: Set<string> = this.model.widgetEngine.userGlobalFilter.get(fkd.fullCode);
        if(vals?.size === 1) {
          rawVal = [...vals][0];
          //console.log('injection val user', fkd.fullCode);
        }
      }
      

      

      this.initKeyPartMap.set(fkd.fullCode, rawVal);
    });
    
    if(cloning) { // en cas de clonage, reprise des valeurs
      [...widgetLine['cloneParts'].keys()].forEach(key => {
  
          let val: any = widgetLine['cloneParts'].get(key);
          this.initKeyPartMap.set(key, val);       
      });
    }


    this.keyPartMap = cloneDeep(this.initKeyPartMap); // on sépare les instances

    // suppression de la ligne de matrice
    this.model.cellMatrixEngine.deleteRow(cloneBaseline.level, cloneBaseline.key, cloneBaseline.scen);

    // création du clone et persistance des values
    this.clone = this.model.dataEngine.baselineToWidgetLine(this.fakeFei, WizConstants.SCEN_FAKE_KEY_CODE, cloneBaseline);


    // alimentation du FEI avec le clone
    let map: Map<string, Map<string, WidgetLine>> = new Map();
    map.set(this.clone.scen, new Map());
    map.get(this.clone.scen).set(this.clone.key, this.clone);
    
    this.fakeFei.lines = map;


    // on rince les éventuelles entrées Dexie
    this.fakeFei.model.cellMatrixEngine.clearDexieMatrixLine(this.clone);
    if(!this.clone.bl?.id) { // mode création
      let tracks: FormKeyUpdate = this.defaultTracks();
      tracks.initMode = true;
      // console.log("feedClone", tracks.initMode);
      if(!cloning) {
        this.feedMasterKeys(tracks); // alimentation possible des champs du maître
      }
      // on recalcule
      FormulaCoreEngine.computeLines(this.fakeFei, [this.clone], true);
      // console.log('create', tracks);
      this.feedDefaultKeys(tracks, this.asyncCheck.bind(this)); // alimentation des champs par défaut
      //this.synchronize(tracks);
    } else {
      FormulaCoreEngine.computeLines(this.fakeFei, [this.clone], true);
      //console.log('form feedClone', this.clone);
    }

  }

  /* ##############################################################################
        cycle de vie et construction graphique
  ############################################################################## */

  ngAfterViewInit() {
    super.ngAfterViewInit();
    /** modification de n'importe quel élément, dispatché par les ffRenderer) */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_UPDATE, this.fieldUpdate);
    /** saisie en cours sur numeric ou datefield */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_KEY, this.keyUpdate);

    /** Sélection externe sur 2x clic dans un pivot */
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.PIVOT_DOUBLE, this.fastSelect);
    /** modification sur le subGrid */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_SUB_LIST_UPDATE, this.subUpdate);
    /** modification d'un message) */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.MESSAGE_UPDATE, this.messageUpdate);

    /** ouverture / fermeture d'un accordéon */
    WizEventManager.addListener(this, this, ALK_EVENT_TYPE.FORM_ZONE_UPDATE, this.manageContainers);
  
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_UPDATE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_KEY);

    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.PIVOT_DOUBLE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_SUB_LIST_UPDATE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.MESSAGE_UPDATE);
    WizEventManager.removeListener(this, this, ALK_EVENT_TYPE.FORM_ZONE_UPDATE);

  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }
    
    if(this.state === WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events

  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {  

    let mustResetSelection: boolean = false; // on est obsolète, on rince
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustDrawFields: boolean = false; // on doit construire les éléments graphiques
    let mustRefresh: boolean = false; // simple refresh sur recalcul
    let mustNextStep: boolean = false; // on contrôle le besoin de création du subGrid
    let mustSelectNewLine: boolean = false; // forcer le refresh en cas de creation

    // première création (ou reset global)
    if(origin === WIDGET_ACTION_ORIGIN.INIT || origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {

      this.reset();

      mustBuildRows = true;
      // mustResetSelection = true;
      mustDrawFields = true;
      // construction de la map des triggers et des valeurs par défaut
      let raw: any[] = this.model.freedataEngine.buildTriggers(this.fei);
      this.triggersMap = raw[0];
      this.reverseTriggersMap = raw[1];
      // alimentation des champs par défaut SANS trigger
      this.defaultMap = new Map(); 
      [...this.triggersMap?.values()].forEach(t => {
        if(!t.triggers || t.triggers.size <= 0) { // pas de trigger
          this.defaultMap.set(t.fkd.fullCode, t);
        } else {
          let isTrig: boolean = false;
          [...t.triggers.keys()].forEach(s => {
            if(s !== WizConstants.TRIGGER_ALWAYS) { // aucun intérêt le always sur baseline
              isTrig = true;
            }
          })
          if(!isTrig) {
            this.defaultMap.set(t.fkd.fullCode, t);
          }
        }
      });


      this.resetOrderedControls();
      this.resetIntegrityControls();
      // this.orderedControls = FeiEngine.builOrderedControls(this.fei);

      mustNextStep = true; // on check le nextStep

    }

    // modification de la configuration
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {
      mustDrawFields = true;
      this.resetNextStepDatas(); // va forcer une reconstruction

      // contrôles de cohérence, on prend tous les éditables ?
      this.resetOrderedControls();
      this.resetIntegrityControls();
      // this.orderedControls = FeiEngine.builOrderedControls(this.fei);
      mustNextStep = true; // on check le nextStep
    }

    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      if(this.state >= WIDGET_STATE.READY) {
        mustBuildRows = true;
        mustNextStep = true;
      }
    }

    // forcer la reselection en cas de create / update sur MOI
    let newKey: string = null;

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let loopResults: Map<FeiVO, LoopResult> = data[0];  
      this.hasUpdate = false;
      // seulement si je suis la cible
      let loop: LoopResult = loopResults.get(this.fei);
      
      if(loop?.deleted?.size > 0) { // synchro en cas de delete (surtout si c'est moi)
        mustBuildRows = true;
        [...loop.deleted.keys()].forEach(wl => {
          if(wl.key === this.currentWidgetLine?.key) {;
            mustResetSelection = true;
          }
        });
      }

      if(loop?.created?.size > 0) { // sur le create, ce n'est plus la même instance en cas de crea
        mustBuildRows = true;
        [...loop.created.keys()].forEach(wl => { 
          if(wl.key === this.clone?.key) { // la currentWidgetLine est toujours naze en cas de création (***)
            newKey = wl.key;
            mustSelectNewLine = true; // reset
            // this.doSelect(exist);
            // mustNextStep = true; // on check le nextStep
          // } else { // retour de create, on rince
          //   mustResetSelection = true; // reset
          } 
        });
      }

      if(loop?.updated?.size > 0) { // modif sur la ligne en cours de modification
        mustBuildRows = true;
        [...loop.updated.keys()].forEach(wl => {
          if(wl.key === this.currentWidgetLine?.key) {
            newKey = wl.key;
            mustSelectNewLine = true; // reset
          }
        });
      }
    }

    // changement de sélection
    if(origin === WIDGET_ACTION_ORIGIN.SELECT) {
      if(this.state >= WIDGET_STATE.READY) {
        // this.hasUpdate = false;
        mustDrawFields = true;
        mustNextStep = true; // on check le nextStep
      }
    }

    // modification du filtre... on vérifie la capacité de création
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      //console.log('globalFiltering on FDForm');
      if(this.mandatoryGlobalEmpty()) {
        mustResetSelection = true;
      }
      this.cdRef.markForCheck();
    }



    if(mustResetSelection) {
      this.reset();
      mustNextStep = true; // on check le nextStep
    }
    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
      if(!this.creationMode) {
        let exist: WidgetLine = null;
        if(this.currentWidgetLine) { // vérifier qu'on est toujours dans le pool
          exist = this.mainDS.getItemByKey(this.currentWidgetLine.key) || null;
        } 
          
        if(!exist) {
          if(this.mainDS?.getWorkedSource()?.length === 1) { // auto select si un seul élément
            exist = this.mainDS.getWorkedSource()[0] ; // autoselect si 1 seul élément
            // mustNextStep = true; // on check le nextStep
          } 
        }

        if(exist != this.currentWidgetLine) {
          this.doSelect(exist);
          mustNextStep = true; // on check le nextStep
        }
        
      }     
    }

    // modification externe sur l'objet en cours d'édition
    if(mustRefresh) {
      this.doCancel(null);
      mustNextStep = true; // on check le nextStep
    }

    // retour de création, forcer la prise de la bonne widgetLine, et refresh du fakeFEI
    if(mustSelectNewLine) {
      let exist: WidgetLine = this.mainDS.getItemByKey(newKey) || null;
      this.doSelect(exist, true);
      // forcer le refresh du subGrid
      this.nextStepRequired(null, true); // on contrôle le nextStep
      // mustNextStep = true; // on check le nextStep
    }



    if(mustDrawFields) {
      // construction graphique, on créé les champs de formulaire
      const widItems: GridsterWidgetItem[] = []; // les champs à construire
      const ffs: WidgetConfigurationItemObj[] = this.context.ff; // la conf gridster : les champs à afficher dans le form

      // alimentation des groupes masqués
      this.feedGroups();

      // en passant par le composant baseline-gridster, on va instancier des ffRenderer
      if (!!ffs && !!this.clone) { // pour chaque champ, mais seulement si j'ai un clone... (sinon écran vide)
        ffs.forEach((wcio: WidgetConfigurationItemObj) => {
          const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, wcio.cod);
          const splittedCode: string = wcio.cod.split(WizConstants.CONTEXT_SEPARATOR)[0]; // pour accepter les champs fakes (champs libres)
          if (!!fkd 
            || splittedCode === ViewData.FORM_ACTION
            || splittedCode === ViewData.FORM_HTML
            || splittedCode === ViewData.FORM_IMAGE
            || splittedCode === ViewData.FORM_ZONE) { // le champ doit exister, sinon on ajoute pas
            const ffo: FormFieldObj = new FormFieldObj(this, this.fakeFei, wcio, false, this.clone);
            const gwi: GridsterWidgetItem = new GridsterWidgetItem(wcio, ffo);
            widItems.push(gwi);
          }
        });
      }

      if(mustNextStep) {
        if(this.state >= WIDGET_STATE.READY) {
          this.nextStepRequired(); // on contrôle le nextStep
        }   
      }

      this.widgetItems = widItems; // pack d'item pour le gridster
      setTimeout(() => {
        this.cdRef.markForCheck();
      }, 0);
    }
  }

  /* ##############################################################################
        saisie dans le formulaire, complétions et contrôles
  ############################################################################## */

  /** annulation des modifs */
  public doCancel(e: WizEvent): void {
    this.saveDisabled = true;
    this.hasUpdate = false;
    this.feedClone(this.currentWidgetLine, this.fakeFei); // creation du clone de travail
    this.alterContent(WIDGET_ACTION_ORIGIN.SELECT); // resélection

    // on signale au fields qu'ils doivent se mettre à jour
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_FIELDS, this.keyPartMap));
  }

  /** construction d'un objet de suivi */
  private defaultTracks(): FormKeyUpdate {
    return { keyUpdates: new Map(), filterKeyUpdates: new Map(), measureUpdates: new Map(), initMode: false };
  }

  /**
   * modification d'une entrée de workcom
   * @param e 
   */
  private messageUpdate(e:AlkEvent): void {
    this.updatedMessage.set(e.data.field, e.data.value);
  }

  /**
   * modification d'un élement de champ du formulaire
   * @param e ALK_EVENT_TYPE.FORM_UPDATE
   */
  private fieldUpdate(e: AlkEvent): void {
    const data: FormUpdate = e.data;
    const ffo: FormFieldObj = data.field; // le champ concerné
    const ffoItem: FormItemObj = data.item; // la valeur sélectionnée pour ce champ
    const fkd: FeiKeyDef = ffo.fkd; // la définition du champ


    this.keyPartMap.set(fkd.fullCode, ffoItem?.value); // alimentation de l'élément modifié
    // console.log('field update', data, this.keyPartMap);
    // 3 cas possibles: 
    // Key (mode création uniquement)
    // filterKey
    // measure

    // objet de suivi
    let tracks: FormKeyUpdate = this.defaultTracks();

    if (fkd.isKey) {
      if (fkd.isFilterKey) { // modification d'une filterKey
        tracks.filterKeyUpdates.set(fkd.fullCode, ffoItem?.value);
        // maj de la baseline ??
        // BaselineEngine.baselineSetFilterKeyValue(this.clone.bl, fkd.fullCode, ffoItem?.value);
      } else { // modification d'une clé (mode création)
        tracks.keyUpdates.set(fkd.fullCode, ffoItem?.value);
        // console.log('update key', tracks.keyUpdates);
        tracks.initMode = true;
        // la structure est potentiellement gigogne, on va supprimer tous les descendants.
        const myCode: string = fkd.code; // clé simple (sans #lvl)
        let myLevel = fkd.level; // référence du level en cours

        let mapCode: string; // code de l'élément en cours de test
        let mapLevel: number; // level de l'élément en cours de test

        // on supprime les descendants
        Array.from(this.keyPartMap.keys()).forEach((key) => {
          if (!!key) {
            mapCode = key.split(ViewData.CONTEXT_SEPARATOR)[0];
            mapLevel = Number(key.split(ViewData.CONTEXT_SEPARATOR)[1]);
            if (mapCode === myCode) { // même souche de Freedata
              if (mapLevel > myLevel) { // et c'est un enfant
                this.keyPartMap.set(key, null); // on rince la valeur de clé
                tracks.keyUpdates.set(key, null);
              }
            }
          }
        });

        // et on alimente en auto tous les antécédents
        if (ffoItem) {
          let referenceLevel: number = myLevel; // niveau de départ pour alimenter les parents: Moi
          let daddyKey: string; // la clé du parent
          // une valeur, on peuple les ascendants (saisie directe par mois, par exemple)
          if (myCode === ViewData.CHRONO) { // chrono
            let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(ffoItem.value);
            while (!!chrono?.parentCode && referenceLevel > 1) { // on remonte la pile de parent du chrono
              referenceLevel--;
              daddyKey = myCode + ViewData.CONTEXT_SEPARATOR + referenceLevel;
              chrono = this.model.chronocodeEngine.getChronocode(chrono.parentCode); // on prend papa
              if (this.keyPartMap.has(daddyKey)) {
                this.keyPartMap.set(daddyKey, chrono.code);
                tracks.keyUpdates.set(daddyKey, chrono.code);
              }
            }
          } else { // Freedata
            let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(myCode, ffoItem.value); // FD a plat, collecte simple
            while (!!fdv?.parentCode) {
              referenceLevel--;
              daddyKey = myCode + ViewData.CONTEXT_SEPARATOR + referenceLevel;
              fdv = this.model.freedataEngine.getFreedataValue(myCode, fdv.parentCode); // on prend papa
              if (this.keyPartMap.has(daddyKey)) {
                this.keyPartMap.set(daddyKey, fdv.code);
                tracks.keyUpdates.set(daddyKey, fdv.code);
              }
            }
          }
        }
      }

    } else { // modification d'une mesure
      tracks.measureUpdates.set(fkd.fullCode, ffoItem?.value);
      // mettre à jour la matrice
      let ibas: IBaselineValue =  this.model.cellMatrixEngine.addCell(this.clone.level, this.clone.key, this.clone.scen, fkd.fullCode, ffoItem?.value);
      this.clone.cells.set(ibas.m, {l: this.clone.level, k: this.clone.key, sc: this.clone.scen, m: ibas.m});

      // maj de la baseline ??
      // BaselineEngine.baselineSetValue(this.clone.bl, fkd.fullCode, ffoItem?.value);
    }

    // on met jour les éléments qui dépendent de triggers
    // console.log('field update', tracks);
    this.feedDefaultKeys(tracks, this.asyncCheck.bind(this)); 

    this.asyncCheck(tracks);
  }

  private asyncCheck(tracks: FormKeyUpdate): void {
    // gestion des locks
    this.checkIntegrity();
    // synchronisation BL & WL + subGrid
    this.synchronize(tracks);
  }


  private synchronize(tracks: FormKeyUpdate): void {
    
    if(tracks.keyUpdates.size > 0) { // modification de structure de clé, on reconstruit le clone et le fakeFei
      // alimentation de la map
      [...tracks.keyUpdates.keys()].forEach(k => {
        this.keyPartMap.set(k, tracks.keyUpdates.get(k));
      });
      
      // modification de la clé de BL
      this.clone.bl.splittedKey = [];
      this.fei.visionKeys.forEach((fkd: FeiKeyDef) => {
        // this.clone.bl.trios[fkd.fullCode] = this.keyPartMap.get(fkd.fullCode);
        this.clone.bl.splittedKey.push(this.keyPartMap.get(fkd.fullCode));
      });
      this.clone.bl.key = this.clone.bl.splittedKey.join(ViewData.KEY_SEPARATOR);
      
      // modification de la clé de clone
      this.clone.key = this.clone.bl.key;
      // suppression des cellules, elles sont obsolètes TODO rincer la matrice ??
      this.clone.cells = new Map();
      
      // modification du FEI
      this.fakeFei.lines = new Map();
      this.fakeFei.lines.set(this.fakeFei.scenario, new Map());
      this.fakeFei.lines.get(this.fakeFei.scenario).set(this.clone.key, this.clone);

    }
    [...tracks.filterKeyUpdates.keys()].forEach(k => {
      BaselineEngine.baselineSetFilterKeyValue(this.clone.bl, k, tracks.filterKeyUpdates.get(k));
    });

    // TODO TODO les baselines sont HS, mauvaise clé & mauvaises valeurs
    // rincer la keyPartMap pour les mesures non-editables 
    // réinjecter des BLVs

    let cleanMap: Map<string, string> = new Map();
    this.clone.bl.values = {};
    [...this.keyPartMap.keys()].forEach(k => {
      let fkd: FeiKeyDef = this.fakeFei.keyMap.get(k);
      // console.log('fkd: ', k, fkd?.isMeasure, fkd?.editable);
      if(!fkd.isMeasure) {
        cleanMap.set(k, this.keyPartMap.get(k));
      } else { // les mesures
        if(fkd.editable) {//} || fkd.datakind === FIELD_KIND.BINARY) { // sinon perte des images
          cleanMap.set(k, this.keyPartMap.get(k));
          BaselineEngine.baselineSetValue(this.clone.bl, k, this.keyPartMap.get(k));
        } else {
          //console.log('no edit', k, fkd);
        }
      }
    });
    this.keyPartMap = cleanMap;
    
    // on poursuit avec le contrôle de cohérence
    // recalcul des mesures du niveau, la baseline a peut-être changée
    this.computeFakeFei(tracks); 
  }


  /** alimentation des props du master si existe, * sinon. UNIQUEMENT en CREATION */
  private feedMasterKeys(tracks: FormKeyUpdate): void {

    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); 
    let wl: WidgetLine; // une source, pour alimentation de ce qu'on peut
    if (master?.visionStep?.level < this.settings.vs) {
      let overlord: IWidget = master; // on va chercher sur le parent direct... mais aussi au dessus s'il est vide
      let src: WidgetLine[] = overlord.mainDS?.selectedItems; // on essaye le selectedItem
      if (src.length <= 0) { // rien dans le master
        src = overlord.mainDS?.getWorkedSource(); // ou tout le pool si pas de sélection
      }

      // si le pool est à 0 (rien dans le master...) on va remonter la pile pour voir si on trouve un truc
      if (src.length <= 0) {
        overlord = this.model.widgetEngine.checkFirstMaster(overlord);
        while (!!overlord && src.length === 0) { // on remonte la pile de parents
          src = overlord.mainDS?.selectedItems;
          overlord = this.model.widgetEngine.checkFirstMaster(overlord);
        }
      }

      if (src?.length === 1) { // on a bouclé sur les ascendants.. Si un seul, on alimente ce qu'on peut
        wl = src[0];
        // reset des maps de clés
        this.initKeyPartMap = new Map();
        this.updatedMessage = new Map();
        // alimentation des maps de valeurs du formulaire depuis la VRAI WidgetLine

        let rawVal: string = null; // la valeur pour la clé de FEI
        this.fei.keys.forEach((fkd: FeiKeyDef) => {
          rawVal = WizConstants.wildcardDomain;
          if((fkd.isKey || fkd.isFilterKey) && !fkd.isScen) { // keys et filters
            rawVal = BaselineEngine.getRawKeyOrValue(overlord.fei, wl, fkd.fullCode); 
            this.initKeyPartMap.set(fkd.fullCode, rawVal);
            this.keyPartMap.set(fkd.fullCode, rawVal);

            if(fkd.isFilterKey) {
              tracks.filterKeyUpdates.set(fkd.fullCode, rawVal);
            } else {
              tracks.keyUpdates.set(fkd.fullCode, rawVal);
            }
          }
        });
      }

      if(wl && this.creationMode){
        let candidateBaseline: Baseline = this.model.widgetEngine.buildBaselineFromPrototype(this.fei, this.keyPartMap);
        let candidateWidgetLine: WidgetLine = this.model.widgetEngine.buildWidgetLinePrototype(this.fei, candidateBaseline);

        this.fei.visionKeys.forEach((fkd: FeiKeyDef) => {
          // si c'est une séquence, on alimente keyUpdates & 
          let rawVal: string = null; // la valeur pour la clé de FEI
          if(fkd.isSequence) {
            rawVal = BaselineEngine.getRawKeyOrValue(this.fei, candidateWidgetLine, fkd.fullCode); 
            this.initKeyPartMap.set(fkd.fullCode, rawVal);
            this.keyPartMap.set(fkd.fullCode, rawVal);
            tracks.keyUpdates.set(fkd.fullCode, rawVal);
          }
        });


        FormulaCoreEngine.computeLines(this.fakeFei, [candidateWidgetLine]);
        this.clone = candidateWidgetLine;
      }

    }
  }

  /**
   * éventuelle alimentation des champs déclenchés par un trigger
   * Appelé sur init et sur modification d'un champ
   * @param tracks: FormKeyUpdate suivi des modifs, null sur init / create
   * @param callback pour appel pseudo-Asynchrone en cas de trigger de type matrice
   */
  private feedDefaultKeys(tracks: FormKeyUpdate, callback?: Function): void {
    
 
    // set des triggers déjà joués
    let playedTriggers: Set<string> = new Set();

    // alimentation initiale, peuplement des champs par défaut & play des triggers
    // une seule fois, si bl.id = null
    if (!!tracks?.initMode) { 
      // console.log('initMode', tracks, firstInit);

      // les valeurs par défaut sans trigger
      [...this.defaultMap?.keys()].forEach((trigKey: string) => {
      // [...this.triggersMap.keys()].forEach((trigKey: string) => {
        playedTriggers.add(trigKey);
        
        let formTrig: Triggers = this.defaultMap.get(trigKey);

        // console.log("default values", trigKey, formTrig.triggers);
        // TODO jouer dans l'ordre fei.orderedMatrix
        // un tour de calcul
        // recompute (après le clear de la matrice, babache) pour gestion des modifs en cascade
        FormulaCoreEngine.computeLines(this.fakeFei, [this.clone], true);
        
        if(formTrig.fkd.isFreedata) { // trigger issus du Freedata
          const triggeredValue: string = this.model.widgetEngine.computeDefaultValue(this.fakeFei, this.clone, formTrig.fkd.fullCode);
          this.keyPartMap.set(formTrig.fkd.fullCode, triggeredValue);
          // on trace pour maj des ffRenderers
          if (!tracks) {
            tracks = this.defaultTracks();
          }
          tracks.filterKeyUpdates.set(formTrig.fkd.fullCode, triggeredValue);
        } else if(formTrig.fkd.isMeasure) {//} && firstInit) {// && !trigKey.startsWith(WizConstants.FAKE_TRIGGER)) { // defaultValue sur une mesure (on ne joue pas les triggers)
          // TODO TODO cycle de vie : maj mesure si existe pas
          const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(formTrig.mm.mmi, this.clone, this.fakeFei, true, this.keyPartMap);

          if(cr?.valor?.length > 0) {
            this.keyPartMap.set(formTrig.fkd.fullCode, cr.valor);

            if (!tracks) {
              tracks = this.defaultTracks();
            }
            tracks.measureUpdates.set(formTrig.fkd.fullCode, cr.valor);
            // mettre à jour la matrice
            let ibas: IBaselineValue =  this.model.cellMatrixEngine.addCell(this.clone.level, this.clone.key, this.clone.scen, formTrig.fkd.fullCode, cr.valor);
            this.clone.cells.set(ibas.m, {l: this.clone.level, k: this.clone.key, sc: this.clone.scen, m: ibas.m});
              
          }
        }
        
      });
    } 

    // console.log("feedDefaultKeys", this.reverseTriggersMap, tracks.initMode);
    // this.model.cellMatrixEngine.clearDexieMatrixLine(this.clone);

    // les modifs de base sur les champs éditables
    let baseUpdated: Set<string> = new Set();
    // les modifs sur les champs calculés
    let updatedMeasures: Map<string, [string, string]>;

    // les triggers qu'on va réellement jouer
    let toPlayTriggers: Set<string> = new Set();


    [...tracks.keyUpdates.keys()].forEach((fkey: string) => {
      baseUpdated.add(fkey);
      if(!playedTriggers.has(fkey)) {
        toPlayTriggers.add(fkey);
      }
      // modif de key ? quoi faire => reconstruire la clé ? // rejouer l'init ?
      // BaselineEngine.baselinese(this.clone.bl, fkey, ue.newValue);
    });
    [...tracks.filterKeyUpdates.keys()].forEach((fkey: string) => {
      baseUpdated.add(fkey);
      if(!playedTriggers.has(fkey)) {
        toPlayTriggers.add(fkey);
      }
      BaselineEngine.baselineSetFilterKeyValue(this.clone.bl, fkey, tracks.filterKeyUpdates.get(fkey));
    });

    [...tracks.measureUpdates.keys()].forEach((fkey: string) => {
      baseUpdated.add(fkey);
      if(!playedTriggers.has(fkey)) {
        toPlayTriggers.add(fkey);
      }
      this.fei.model.cellMatrixEngine.addCell(this.clone.level, this.clone.key, this.clone.scen, fkey, tracks.measureUpdates.get(fkey));

    });
    // console.log('keyPartMap', this.keyPartMap, baseUpdated);

   // étape 2, je recalcul et collecte les mesures modifiées 
   // va pas ramener asser... Tout jouer ?

   // TODO tester les modifs récente sur le clone, pas les keypartMap
   // comparer keypartMap & track ? ou clone ?
   updatedMeasures = FormulaCoreEngine.checkRecurseTriggers(this.fakeFei, this.clone) || new Map();
   [...updatedMeasures.keys()].forEach(k => {
    if(!playedTriggers.has(k)) {
      toPlayTriggers.add(k);
    }
    this.keyPartMap.set(k, updatedMeasures.get(k)[1]);
   });

  // console.log("packs", baseUpdated, updatedMeasures, playedTriggers, toPlayTriggers);

  // les éventuels triggers. Je regarde déjà sur ma cellule modifiée
  let reverseTriggers: Set<Triggers> = new Set();

  [...this.reverseTriggersMap.keys()].forEach(tk => { 
    if(toPlayTriggers.has(tk)) {
    // console.log('tk', tk);
      let morrTriggers: Set<Triggers> = this.reverseTriggersMap.get(tk);
      [...morrTriggers.keys()].forEach(t => {
        reverseTriggers.add(t);
        // console.log('morrTriggers', t.fkd.fullCode, t.defaultValue, t.triggers);
      });
    } else {
      // console.log('not tk', tk);
    }
  });

  // on rince le cache dexie si l'un des triggers est asynchrone
  if(reverseTriggers?.size > 0) {
    [...reverseTriggers.values()].forEach(trig => {
      if(!!trig.mm?.asyncDeps) {
        // console.log('async deps', trig);
        this.model.cellMatrixEngine.clearDexieMatrixLine(this.clone);
      }
    });
  };

  // on boucle sur les triggers
  // il faut collecter les mesures modifiées pour jouer les triggers en cascade (M_CLTRIG#I)

  // prise des valeurs modifiées par trigger
  if(reverseTriggers?.size > 0) {

      const linePack: WidgetLine[] = [this.clone];

      [...reverseTriggers.values()].forEach(trig => {
        
        const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(trig.mm.mmi, this.clone, this.fakeFei, true, this.keyPartMap);
        // console.log("track feedDefaultKeys", fkey, cr?.valor);
        if(cr?.valor?.length > 0) {
          this.keyPartMap.set(trig.fkd.fullCode, cr.valor);
          // console.log('update mesure form trigger', fkey,formTrig.fkd.fullCode, cr.valor);
          tracks.measureUpdates.set(trig.fkd.fullCode, cr.valor);
          // mettre à jour la matrice
          let ibas: IBaselineValue =  this.model.cellMatrixEngine.addCell(this.clone.level, this.clone.key, this.clone.scen, trig.fkd.fullCode, cr.valor);
          this.clone.cells.set(ibas.m, {l: this.clone.level, k: this.clone.key, sc: this.clone.scen, m: ibas.m});
        }     
      });
  }


    // il existe des appels de matrice, il faut refaire un passage après le chargement
    if(this.fei.model.cellMatrixEngine.dexiePromises.size > 0) {
      // console.log('matrix trigger', this.fei.model.cellMatrixEngine.dexiePromises);
      this.checkTriggersWithMatrix(tracks, callback);
    } else {
      if(!!callback) {
        callback(tracks);
      }
    }
  }

  private checkTriggersWithMatrix(tracks: FormKeyUpdate, callback?: Function): void {
    // console.log('checkTriggersWithMatrix', tracks);
    
      // console.log('postControlWithMatrix', updates);
      Promise.all(this.fei.model.cellMatrixEngine.dexiePromises.values()).then((matrix: MatrixCallParam[]) => {
        matrix.forEach((mat:MatrixCallParam) => {
          // console.log('computeWithMatrix', mat);
          let cell: WidgetCell = mat.widgetCell;
          let res: any = null;
          if(mat.result?.length > 0) {
            // console.log('matrix', matrix);
            let rawResult: string = mat.result[0][mat.targetColumn];
            // correction des formats numérique
            if(rawResult !== null && rawResult!==undefined) { 
              if(this.fei.model.dataEngine.datakindIsNumeric(mat.datakind)) {
                res = Number(rawResult.replaceAll(',','.').replaceAll(' ',''));
              } else {
                res = rawResult;
              }
              // console.log('matrix', mat.datakind, res);
            }
          }
          this.fei.model.cellMatrixEngine.dexieResolved.set(cell, res);
          // mise à jour de l'entrée
          
          // TODO TODO identifier la mesure source
          let baseCode: string = cell.m.split(WizConstants.CONTEXT_SEPARATOR)[0];
          let srcMeasureCode = this.triggersMap.get(baseCode).fkd.fullCode;
          BaselineEngine.baselineSetValue(this.clone.bl, srcMeasureCode, res);
          let ibas: IBaselineValue = this.model.cellMatrixEngine.addCell(this.clone.bl.level, this.clone.bl.key, this.clone.bl.scen, srcMeasureCode, res);
          this.clone.cells.set(ibas.m, {l: this.clone.level, k: this.clone.key, sc: this.clone.scen, m: ibas.m});
          tracks.measureUpdates.set(srcMeasureCode, res);
          this.keyPartMap.set(srcMeasureCode, res);
          // et on peut supprimer l'entrée
          this.fei.model.cellMatrixEngine.dexieResolved.delete(cell);

        });
        // on rince les promesses
        this.fei.model.cellMatrixEngine.dexiePromises.clear();

        if(!!callback) {
          callback(tracks);
        }
      });
  }

  /**
   * une modif en cours sur un compo type dateField ou numéric, on affiche save & cancel
   * @param e 
   */
  private keyUpdate(e:WizEvent): void {
    this.hasUpdate = true;
    this.checkSave();
  }


  /**
   * les erreurs en cours, alimentées sur save
   * sous la forme pkd.fullCode = errorString
   * Attention, utilisé par les ffRenderers
   */
  public errorSet: Map<string, string> = new Map();

  /** 
   * contrôler l'activation du bouton save sur modifs 
   * modif d'une valeur de ffRenderer (measure)
   * @param tracks: FormKeyUpdate suivi des modifs, null sur init / create
   * @param initMode: changement de WidgetLine / demande de création
   */
  private checkSave(): void {

    // contrôle d'intégrité sur l'ensemble des champs (et gestion min & max)
    this.errorSet = new Map(); // on rince le set d'erreurs
      
    let rawErrorSet: Map<Measure, string> = this.model.widgetEngine.computeIntegrity(this.fakeFei, this.orderedControls, [this.clone], WizConstants.CHECK_MODE_CONTROL);

    if (rawErrorSet?.size > 0) {
      [...rawErrorSet.keys()].forEach(meas => {
        this.errorSet.set(meas.shortCode, rawErrorSet.get(meas));
      });
      
        // faire un set sur les libellés, possiblement le même...
        const maxDisplayErrors =  [...rawErrorSet.keys()].slice(0,2);
        
        maxDisplayErrors.forEach(meas => {
          let cleanMess: string = this.model.measureEngine.getMeasureLabel(meas);
          cleanMess = cleanMess  + '<br>';
          cleanMess +=  rawErrorSet.get(meas);
          WizPopUp.toast(cleanMess, WizIcon.TOAST_ERROR).show();
        });
    }

    this.hasUpdate = true; // sinon, on a bien une modif
    let mustBeDisabled: boolean = false; // contrôle de cohérence pour activation du save
    this.fei.visionKeys.forEach((fkd: FeiKeyDef) => {
      if(!fkd.isSequence) { // pas de contrôle sur les séquences
        if ( !this.keyPartMap.get(fkd.fullCode) || 
              this.keyPartMap.get(fkd.fullCode) === ViewData.wildcardDomain || 
              this.keyPartMap.get(fkd.fullCode) === ViewData.err) {
          mustBeDisabled = true;
        }
      }

    });
    if (this.errorSet?.size > 0) { // une erreur sur une mesure
      mustBeDisabled = true;
    }

    this.saveDisabled = mustBeDisabled;

  }

  /**
   * Mise à jour du fakeFei avec la baseline du clone. 
   * Puis calcul
   */
  private computeFakeFei(tracks: FormKeyUpdate): void {
    const baseline: Baseline = this.clone.bl; // la baseline en cours de calcul
    // console.log('computeFakeFei', baseline, this.fakeFei.model.cellMatrixEngine.dexieResolved);
    this.fakeFei.model.cellMatrixEngine.clearDexieMatrixLine(this.clone);
    FormulaCoreEngine.computeLines(this.fakeFei, this.fakeFei.getAllLines(), true, this.backFromCompute.bind(this, tracks));
  }

  private backFromCompute(tracks: FormKeyUpdate, fakeFei: FeiVO): void {

    // et mise à jour de la map
    fakeFei.orderedMatrix.forEach((mm: MeasMode) => {
      let result: string = BaselineEngine.getValue(fakeFei, this.clone, this.clone.cells.get(mm.code));
      // if(mm.code === 'OAM_TIME-FRN-LBL#I' || mm.code === 'OAM_TIME-KEY#I') {
      //   console.log(mm.code, result);
      // }
      
      if ((!mm.m.editable || mm.mm !== WizConstants.MEASURE_MODE_INTERACT) // pas éditable ou pas I
        && this.keyPartMap.get(mm.code) !== result) // et calcul différent
        {
          //if(mm.m.dataKind !== FIELD_KIND.BINARY) { // sinon on perd les images
            this.keyPartMap.set(mm.code, result);
          //}
          //console.log('computeFakeFei', mm.code, result);   
      }
    });

    // on alimente éventuellement le nextStep
    this.nextStepRequired(tracks);

    // on insiste sur les clés, en cas de changement de structure
    if(tracks.initMode) {
      // console.log('backFromCompute initMode ??', tracks);
      this.feedDefaultKeys(tracks); 
    }
    
    // sur fin de contrôle, dispatch d'un event pour mettre à jour les fields si leur valeur est # de keyPartMap
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_REFRESH_FIELDS, this.keyPartMap));

    // on termine par le contrôle de validation de sauvegarde
    this.checkSave();

  }

  /**
   * demande d'enregistrement
   * @param e 
   * @returns 
   */
  public doValid(e: AlkEvent): void {

    let updates: UpdateEntry[] = []; // les updates / creates

    // contrôle d'intégrité sur l'ensemble des champs (et gestion min & max)
    this.errorSet = new Map(); // on rince le set d'erreurs
      
    let rawErrorSet: Map<Measure, string> = this.model.widgetEngine.computeIntegrity(this.fakeFei, this.integrityControls, [this.clone], WizConstants.CHECK_MODE_INTEGRITY);

    if (rawErrorSet?.size > 0) {
      [...rawErrorSet.keys()].forEach(meas => {
        this.errorSet.set(meas.shortCode, rawErrorSet.get(meas));
      });
      
        // faire un set sur les libellés, possiblement le même...
        const maxDisplayErrors =  [...rawErrorSet.keys()].slice(0,2);

        maxDisplayErrors.forEach(meas => {
          let cleanMess: string = this.model.measureEngine.getMeasureLabel(meas);
          cleanMess = cleanMess  + '<br>';
          cleanMess +=  rawErrorSet.get(meas);
          WizPopUp.toast(cleanMess, WizIcon.TOAST_ERROR).show();
        });
        console.log('form errors', rawErrorSet);
        return;
    }



    if (this.creationMode) { // mode création     
      let candidateBaseline: Baseline = this.model.widgetEngine.buildBaselineFromPrototype(this.fei, this.keyPartMap);
      let candidateWidgetLine: WidgetLine = this.model.widgetEngine.buildWidgetLinePrototype(this.fei, candidateBaseline);
      // contrôle d'unicité, la ligne ne doit pas exister
      let dlo: DataLoadObj = this.model.widgetDataService.getDataloadByKey(this.fei.visionCode, this.fei.visionStepLevel, this.fei.scenario);
      if(dlo.localBaselines[candidateBaseline.key]) {
        WizPopUp.toast('$.baseline.create.error.exist.$', WizIcon.TOAST_WARNING).show();
        return;
      } else {
        let createEntry: UpdateEntry = this.model.dataEngine.generateUpdate(this.fei, candidateWidgetLine, null, null);
        updates.push(createEntry);

        // maj du clone pour obtenir la bonne key en cas de séquence
        this.clone.bl.key = candidateBaseline.key;
        this.clone.key = candidateBaseline.key;

      }

    } else { // mode update
      let deltaPart: Map<string, string> = new Map();
      // on compare l'init et l'actif, pour ne générer que le delta
      let currentVal: string;
      let initVal: string;
      [...this.keyPartMap.keys()].forEach((key: string) => {
        currentVal = this.keyPartMap.get(key);
        initVal = this.initKeyPartMap.get(key);

        // on cherche un vrai delta (null <> null)
        if(currentVal !== initVal && (!!currentVal || !!initVal)) {
          deltaPart.set(key, this.keyPartMap.get(key)); 
        }

        // gestion des modifs sur les messages
        if(this.updatedMessage.has(key)) {
          // console.log('updatedMessage', key);
          deltaPart.set(key, this.keyPartMap.get(key)); 
        }
      });

      // attention, il y a des faux positifs lors de la mise à jour d'une ligne de scenar, sur les héritages de valeurs
      // => elles sont à null pour cette BL
      updates = this.model.widgetEngine.updateBaselineFromPrototype(this.fei, this.currentWidgetLine, deltaPart, this.updatedMessage);
    }

    if(this.nextStepFei) { // prise des éléments du subForm
      this.feedNextStepUpdates(updates);
    }

    this.saveDisabled = true;
    // dispatch pour autoclose en cas d'appel depuis Calendar.addPop
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.FORM_SAVED, this));
    const csp: CompositeSourcePack =  { updates };
    this.model.compositeEngine.compositeCommit(csp);
  }

  /* #####################################
      gestion de la suppression
     ##################################### */

  /** annulation de la sélection (retour à vide) */
  public askForUnselect(): void {
    this.doSelect(null);
  }

  /** suppression autorisée */
  public mayDelete(): boolean {
    if(this.creationMode) {
      return true;
    }

    if (this.currentWidgetLine && // y'a une ligne
      (!!this.context.shd || !!this.context.shrd) && // et droits en suppression ou suppression en cascade
      this.fei.canDeleteItem([this.currentWidgetLine], true)) { // gestion des droits vsps
      return true;
    }
    return false;
  }

  /** click sur l'icone de suppression */
  public askForDelete(): void {

    if(this.creationMode) { // mode création, pas de problème
      this.doDelete(ConfirmationResultObj.build(CONFIRMATION_RESULT.CONFIRM, this.currentWidgetLine));
      return;
    }

    const integrityMeas: Measure[] = [];
    this.context.i?.forEach((confItem: WidgetConfigurationItemObj) => {
      const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, confItem.cod);
      if (!!fkd && fkd.isMeasure) {
        integrityMeas.push(fkd.measure);
      }
    })
    // contrôle d'intégrité KO
    if (integrityMeas.length > 0 && !!this.context.idel &&
      !this.integrityCheck(this.fei, integrityMeas, [this.currentWidgetLine])) {
      return;
    }

    WizPopUp.confirm(
      '$.common.delete.title.$',
      '$.common.delete.detail.$',
      this.doDelete.bind(this),
      CONFIRMATION_MODE.CONFIRM_CANCEL,
      this.currentWidgetLine,
      null,
      null
    ).show();

  }

  /**
   * application de la suppression
   * @param confirmationResult 
   */
  private doDelete(confirmationResult: ConfirmationResultObj): void {
    if (confirmationResult.result === CONFIRMATION_RESULT.CONFIRM) {
      if(this.creationMode) {
        this.doSelect(null); // va tout rincer
      } else {
        
        this.currentWidgetLine.bl.operation = EM_ACTION.DELETE;
        let deletecascade: boolean = this.context.shrd === true;
        let updates: UpdateEntry[] = [];
        let update: UpdateEntry = this.model.dataEngine.generateUpdate(this.fei, this.currentWidgetLine, null, null);
        if(deletecascade) {
          update.baseline.deleteCascade = true;
          update.baseline.cascadeDeep = this.model.dataEngine.getMaxDeep(this.fei.visionStepLevel);
        }
        updates.push(update);

        const csp: CompositeSourcePack =  { updates };
        this.model.compositeEngine.compositeCommit(csp, this.backFromDelete.bind(this));
      }
    }
  }

  /** retour de service de commit sur suppression (en cascade ou pas) */
  private backFromDelete(csp: CompositeSourcePack): void {
    this.doSelect(null);
  }

  /* #####################################
      gestion du sous-formulaire
     ##################################### */

  // /** fei de niveau inférieur, pour contrôles de cohérence et alimentation  */
  // public nextStepFei: FeiVO = null;
  /** clone de travail du niveau inférieur */
  public nextStepFakeFei: FeiVO = null;

  /** 
   * le prototype de parent actuel pour le nextStep
   * porte les clés de vision du clone de travail
   * en cas de delta, reset sur la sous-collection
   */
  public nextStepPrototype: Map<string, string> = null;

  // aucune colonne éditable sur le sous-form
  // public zeroColumns: boolean = false; // TODO aucun intérêt

  /** affichage du subGrid, si demandé dans la configuration (ne suffit pas, peut être conditionné par les données) */
  public get showNextStep(): boolean {
    return     !!this.settings?.nxstep // le mode est activé
            && !!this.nextStepFakeFei // on a créé le step
            && !!this.currentWidgetLine // on a un élément de travail dans le formulaire
            && !this.zeroColumns; // et au moins une colonne éditable dans le sous-grid
  }

  /** modif sur le sous-grid */
  private subUpdate(e: AlkEvent): void {
    this.checkSave();
  }

  /**
   * suppression complète du nextStep
   */
  private resetNextStep(): void {
    this.nextStepFei = null;
    this.nextStepFakeFei = null;
    this.resetNextStepDatas();
  }

  /**
   * suppression des données du nextStep
   */
  private resetNextStepDatas(): void {
    this.nextStepPrototype = null;
  }

  /**
   * construction / reconstruction du sous-grid
   * 
   * @param tracks en cas de modif de la structure de clé
   * @param force pour forcer le refresh en cas de mise à jour self (create / update)
   * @returns 
   */
  private nextStepRequired(tracks?: FormKeyUpdate, force: boolean = false): void {
    if(!this.settings?.nxstep) { // pas autorisé
      this.resetNextStep();
      return;
    }

    if(!(this.context.d?.length > 0)) { // on a rien configuré
      this.resetNextStep();
      return;
    }

    if(!this.clone) { // pas d'élément de travail
      this.resetNextStepDatas();
      return;
    }

    if(!this.nextStepFei || tracks?.keyUpdates?.size > 0 || force) { // on charge le step suivant, si nécessaire (en cas de modif de structure de clé)
      this.nextStepFei = this.model.dataEngine.getFei(this.fei.computeMode, this.fei.visionStepLevel + 1, this.fei.scenario) as FeiVO; 
      // création du nextStepFakeFei, c'est lui qui portera les calculs
      this.nextStepFakeFei = this.model.dataEngine.generateFakeFEI(this.nextStepFei);
      this.nextStepPrototype = null;
    }
    

    // TODO identifier un delta dans l'affichage
    // if(!this.nextStepPrototype) {
    this.feedNextStep();
    // }
  }

  /**
   * alimentation du prototype de ligne depuis les visionKeys du clone
   * alimentation de la collection de ligne du nextStepFei
   */
  private feedNextStep(): void {

    let keys: VisionKey[] = BaselineEngine.getStepKeys(this.vision, this.visionStep);
    this.nextStepPrototype = new Map();
    keys.forEach((key: VisionKey) => {
      this.nextStepPrototype.set(key.code, this.keyPartMap.get(key.code));
    });

    let widgetLines: WidgetLine[] = this.generateNextStep(); // construction des lignes théoriques du nextStep

    // alimentation du nextStepFakeFei
    let map: Map<string, Map<string, WidgetLine>> = new Map();
    map.set(this.clone.scen, new Map());
    widgetLines.forEach((wl) => {
      map.get(this.clone.scen).set(wl.key, wl);
    });
    
    this.nextStepFakeFei.lines = map;
    FormulaCoreEngine.computeLines(this.nextStepFakeFei, widgetLines, true);
    this.feedSubGrid(); // alimentation du grid
  }

  /**
   * construit les widgetLines théoriques du nextStep
   * @returns les prototypes WidgetLine[]
   */
  private generateNextStep(): WidgetLine[] {
    // création d'une structure compatible avec le générateur de ligne (IncExcEngine)
    const meMap: Map<string, Baseline> = new Map(); // la baseline en cours de modifications
    const allMap: Map<string, Map<string, string>> = new Map(); // les prototypes de clés pour les enfants: bl.key = <fd/chrono.key> = value

    let candidateBaseline: Baseline = this.model.widgetEngine.buildBaselineFromPrototype(this.fakeFei, this.keyPartMap);

    meMap.set(candidateBaseline.key, candidateBaseline); // ajout de la ligne en cours à la map de précurseurs

    const noLevelKeyMap: Map<string, string> = new Map(); // Map des visionKey SANS LEVEL, pour création des prototypes d'enfants
    let visionKeys = BaselineEngine.getStepKeys(this.vision, this.visionStep);
    
    [...visionKeys.values()].forEach((vk: VisionKey) => {
      let splitCode: string[] = vk.code.split(WizConstants.CONTEXT_SEPARATOR);
      noLevelKeyMap.set(splitCode[0], this.nextStepPrototype.get(vk.code));
    });
    allMap.set(candidateBaseline.key, noLevelKeyMap);
    

    // génération des prototypes de baselines 
    let nextStep: VisionStep = this.nextStepFei.visionStep;
    let keySize : number = BaselineEngine.getStepKeys(this.vision, this.visionStep).length;
    const prototypes: Map<string, Baseline> = this.model.incExcSampleEngine.buildStepLine(this.vision, nextStep, null, null, meMap, allMap, keySize);

    let widgetLines: WidgetLine[] = []; // le pool de fake en préparation
    
    // prise des BLs persitées
    const linesFromBDD: Set<string> = FormulaCoreEngine.getPersitedMeasures(this.nextStepFei);
    // new Set(this.nextStepFei.orderedMatrix.map(item => item.code));  
    
    [...prototypes.values()].forEach((bl: Baseline) => {
      // on force le scenar sur les prototypes de Bls
      bl.scen = this.nextStepFakeFei.scenario;
      let wl: WidgetLine = this.model.widgetEngine.buildWidgetLinePrototype(this.nextStepFakeFei, bl);
      
      // recherche de l'existant pour reprise des mesures (sur le scenar du nextStepFei, pas le fake)
      let existWL: WidgetLine = this.nextStepFei.getWidgetLineForKey(this.nextStepFei.scenario, wl.key);
      if(existWL) {
        // FormulaCoreEngine.computeLines(this.nextStepFei, [existWL]); // on la calcule pour marquer les dépendances
        this.model.dataEngine.mergeWidgetLine(this.nextStepFei, existWL, wl, linesFromBDD);  // on merge les mesures

        // et on reprend les filterKeys
        if(existWL.bl.fkeyjson) {
          wl.bl.fkeyjson = clone(existWL.bl.fkeyjson);
        }
        

      }

      widgetLines.push(wl);
    });
    return widgetLines;
  }

  /**
   * alimentation du subGrid avec le nextStepFEI
   */
  private feedSubGrid(): void {

    this.zeroColumns = true; // par défaut, on considère qu'on est non éditable... jusqu'à preuve du contraire

    // collecte du contexte du step inférieur, pour construction des colonnes
    this.agNextStep.vision = this.nextStepFakeFei.vision;
    this.agNextStep.visionStep = this.nextStepFakeFei.visionStep;
    this.agNextStep.model = this.model;
    this.agNextStep.parentWidget = this;
    // construction ad-hoc d'une configuration pour le subGrid
    const mecon: WidgetConfigurationObj = this.agNextStep.generateNewContext(this.agNextStep.widgetKind);
    const meSort: any[] = [];

    mecon.r = [];
    mecon.c = []; // rien en colonne

    let stepOnlyKeys: VisionKey[] = BaselineEngine.getStepOnlyKeys(this.nextStepFakeFei.vision, this.nextStepFakeFei.visionStep);

    stepOnlyKeys.forEach(item => { // les identifiants en ligne pour les sub-Baselines
      mecon.r.push(this.model.widgetEngine.newWcio(item.code, item.order));
      meSort.push({ colId: item.code, sort: 'desc' }); // ajout des clés de tri
    });

    let realCols: WidgetConfigurationItemObj[] = []; // les colonnes qu'on garde
    // tentative d'alimentation des colonnes "Data"
    this.context.d?.forEach((wcio: WidgetConfigurationItemObj) => {  // pour toutes les colonnes demandées sur le sous-formulaire    
      
      let fkd: FeiKeyDef = this.nextStepFei.keyMap.get(wcio.cod);
      if (fkd.isMeasure) {
        if (fkd.measure.editRestrict) { // contrôle des restrictions, même sur les colonnes non éditables
          const allowed : boolean = this.model.widgetEngine.computeEditRestrict(this.fakeFei, fkd.measure, [this.clone], true, this.keyPartMap);

          if (allowed) { // autorisé
            if (fkd.measure.editable) {
              this.zeroColumns = false; // on a à minima une colonne éditable
            }
            realCols.push(wcio);
          }
        } else { // pas de restriction, on ajoute
          if (fkd.measure.editable) {
            this.zeroColumns = false; // on a à minima une colonne éditable
          }
          realCols.push(wcio);
        }
      } else { // pas une mesure, on ajoute
        realCols.push(wcio);
      }

      // realCols.push(wcio);
    });

    mecon.d = realCols; //  ici, choix des colonnes selon conditions (restrict)
    mecon.d.forEach((wcio: WidgetConfigurationItemObj) => { // neutralisation de l'aggrégat. TODO, pas l'alimenter  
      wcio.agg = null;
    });

    mecon.sort = meSort;
    // alimentation "physique" du subGrid
    this.agNextStep.feedFromForm(this.nextStepFei, this.nextStepFakeFei, mecon);
  }


  public getBestHeight(): string {
    let result: string = '100%';
    if(this.showNextStep) {
      result = '50%';
    }
    //console.log("getBestHeight", result);
    return result;

  }

  public getBestMinHeight(): string {
    let result: string = null;
    if(this.showNextStep) {
      result = '200px';
    }
    //console.log("getBestMinHeight", result);
    return result;

  }


  /**
   * création des entrées d'update pour le sous-formulaire
   * @param updates les updates en cours
   */
  private feedNextStepUpdates(updates: UpdateEntry[]): void {

    let nextStepUpdateMap = this.agNextStep.localUpdateMap;

    let subUpdates: UpdateEntry[] = [];

    let realWL: WidgetLine = null;
    [...nextStepUpdateMap.keys()].forEach((fakeWL: WidgetLine) => {
      realWL = this.nextStepFei.getWidgetLineForKey(this.nextStepFei.scenario, fakeWL.key);
      if(realWL) { // on est sur de l'udpate
        [...nextStepUpdateMap.get(fakeWL).keys()].forEach((col: Column) => {
          let fkd: FeiKeyDef = (col.getColDef() as WizColDef).fkd;
          let value: string = nextStepUpdateMap.get(fakeWL).get(col);

          subUpdates.push(this.model.dataEngine.generateUpdate(this.nextStepFei, realWL, fkd, value));

        });
      } else { // création
        let candidateBaseline: Baseline = BaselineEngine.cloneBaselineForNewScenario(fakeWL.bl, this.nextStepFei.scenario);
        Object.values(fakeWL.bl.values || {}).forEach((blv: BaselineValue) => {
          candidateBaseline.values[blv.mkey] = BaselineEngine.cloneBaselineValueForNewScenario(blv, this.nextStepFei.scenario);
        });
        let candidateWidgetLine: WidgetLine = this.model.widgetEngine.buildWidgetLinePrototype(this.nextStepFei, candidateBaseline);
        subUpdates.push(this.model.dataEngine.generateUpdate(this.nextStepFei, candidateWidgetLine, null, null));
      }
    });

    if(subUpdates.length > 0) {
      subUpdates.forEach(u => {
        updates.push(u);
      });
    }
  }

  /** demande d'export depuis le bouton */
  public exportDatas(kind?: string): void {
    if(!kind || kind === 'excel') {
      // pas d'export Excel pour lui this.model.excelExportEngine.exportAsExcel('allkeyme', this);
    } else {
      this.model.excelExportEngine.exportAsPdf('allkeyme', this, this.argsZone.elementRef, null, null);
    }
  }

 /* ##############################################################################
      gestion des groupes de champs
  ############################################################################## */

  public restrictSet: Map<string, string> = new Map(); // sur checkSave, contrôle des champs non éditables (masqués)

  // map des groupes masqués
  public hiddenGroups: Map<string, boolean> = new Map();


  private feedGroups(): void {
    // console.log('feedGroups', this.context?.sel);
    
    this.context?.sel?.forEach(ws => {
      this.hiddenGroups.set(ws.k, true);
    });
  }

  public manageContainers(e: AlkEvent): void {
    
    // let previousHidden: Map<string, boolean> = clone(this.hiddenGroups);
    
    let data: FormFieldObj = e.data.data;
    let state: boolean = e.data.value; // true = masqué
    let grp: string = data.wcio.pgrp;

    let realChange: boolean = false;
    if(!this.hiddenGroups.has(grp) && !!state) {
      realChange = true;
    } else {
      if(!!state && !!this.hiddenGroups.get(grp)) {
        // 2 positifs
      } else if (!state && !this.hiddenGroups.get(grp)) {
        // 2 négatifs
      } else {
        realChange = true;
      }
    }

    this.hiddenGroups.set(grp, state);

    if(realChange) {
      const hiddens: WidgetSelect[] = [];
      [...this.hiddenGroups.keys()].forEach((key: string) => {
        if(!!this.hiddenGroups.get(key)) {
          hiddens.push({k:key, v:"1"});
        }
      });

      this.context.sel = hiddens; 
      // console.log('form manageContainers', this.context.sel);
      this.updateContext(this.context, true);
    }

    //console.log("form manageContainers", this.context.sel, state);
    // WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.FORM_ZONE_APPLY, this.hiddenGroups)); // dispatch open/ close pour les compos
  }

  private checkIntegrity():void {
    //console.log('checkIntegrity');
  }

}
